import React from 'react'
import { useDispatch } from 'react-redux'
import { TabButton, Tabs } from '@mobi/component-library/Navigation/Tabs'
import { useAppSelector } from '@mobi/betslip/Store/hooks'
import { selectBetSlipItems } from '@mobi/betslip/Store/Bets/selectors'
import {
  hasEnoughItemsToCreateValidMulti,
  hasInvestment,
  isSelectionAllowedInMulti,
  isValidMulti,
} from '@mobi/betslip/helpers/state'
import {
  selectIsBettingSectionExpanded,
  selectVisibleBettingSection,
  selectWorkflowStatus,
} from '@mobi/betslip/Store/Workflow/selectors'
import { setBettingSectionExpandedState } from '@mobi/betslip/Store/Workflow'
import { useListenForDesiredBettingSection } from './hooks/useListenForDesiredBettingSection'
import { filterSingleItemsForWorkflow } from '../Singles/helpers/filterSingleItemsForWorkflow'

export const TabButtons: React.FC<{
  containerRef: React.RefObject<HTMLDivElement>
  singlesRef: React.RefObject<HTMLDivElement>
  multiRef: React.RefObject<HTMLDivElement>
}> = ({ containerRef, singlesRef, multiRef }) => {
  const dispatch = useDispatch()

  const items = useAppSelector(selectBetSlipItems)
  const visibleBettingSection = useAppSelector(selectVisibleBettingSection)
  const isBettingSectionExpanded = useAppSelector(selectIsBettingSectionExpanded)

  const workflowStatus = useAppSelector(selectWorkflowStatus)
  const multiInvestment = useAppSelector(state => state.betslip.bets.multiInvestment)
  const multiBetError = useAppSelector(state => state.betslip.bets.multiBetError)

  const multiItems = items.filter(item => isSelectionAllowedInMulti(item.selection))
  const activeMultiItems = multiItems.filter(item => item.isInMulti)

  const singleItemsForWorkflow = filterSingleItemsForWorkflow(items, workflowStatus)

  const isEditable = workflowStatus === 'ready'
  const isMultiValid = isValidMulti(multiInvestment, multiBetError, activeMultiItems)
  const hasEnoughMultiItems = hasEnoughItemsToCreateValidMulti(multiItems)

  const shouldRenderSinglesTab = isEditable || (!isEditable && items.some(hasInvestment))
  const shouldRenderMultiTab = (isEditable && hasEnoughMultiItems) || (!isEditable && isMultiValid)

  const handleTabClick = React.useCallback(
    (elRef: React.RefObject<HTMLDivElement>) => {
      const top = getRelativeScrollTopOffset(elRef.current as HTMLDivElement)
      containerRef.current?.scrollTo({ top, behavior: 'smooth' })
    },
    [containerRef]
  )

  const scrollToMulti = React.useCallback(() => {
    if (!isBettingSectionExpanded.Multi) {
      dispatch(setBettingSectionExpandedState({ Multi: true }))
      setTimeout(() => handleTabClick(multiRef), 450)
      return
    }
    handleTabClick(multiRef)
  }, [handleTabClick, multiRef, isBettingSectionExpanded, dispatch])

  const scrollToSingles = React.useCallback(() => {
    dispatch(setBettingSectionExpandedState({ Singles: true }))
    handleTabClick(singlesRef)
  }, [handleTabClick, singlesRef, dispatch])

  useListenForDesiredBettingSection({ scrollToMulti, scrollToSingles })

  const isSinglesTabSelected =
    !isBettingSectionExpanded.Multi ||
    (visibleBettingSection === 'Singles' && isBettingSectionExpanded.Singles)

  const isMultiTabSelected =
    !isBettingSectionExpanded.Singles ||
    (visibleBettingSection === 'Multi' && isBettingSectionExpanded.Multi)

  return (
    <Tabs>
      {shouldRenderSinglesTab && (
        <TabButton
          content={`Singles (${singleItemsForWorkflow.length})`}
          onClick={scrollToSingles}
          isSelected={isSinglesTabSelected}
          shouldFillSpace={false}
        />
      )}

      {shouldRenderMultiTab && (
        <TabButton
          content='Multi (1)'
          onClick={scrollToMulti}
          isSelected={isMultiTabSelected}
          shouldFillSpace={false}
        />
      )}
    </Tabs>
  )
}

// =============
// Local Helpers
// =============

function getRelativeScrollTopOffset(el: HTMLDivElement): number {
  const parent = el.parentElement as HTMLDivElement
  return el.offsetTop - parent.offsetTop
}
